import { useMemo } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Placement } from '@Components/designConstants';
import './ViewPlacementMetadata.scss';

const STANDARD_TRACKING_PIXEL_MATCHERS = [
  'https://match.prod.bidr.io/cookie-sync/blisspoint?buyer_user_id={{er_session}}',
  'https://0sqwpj551b.execute-api.us-west-2.amazonaws.com/prod/pixel'
];

interface Creative {
  isci: string;
  creative: string;
  length: number;
  creativeClickthroughUrl: string | null;
  audioCompanion: string | null;
}

interface ViewPlacementMetadataProps {
  placementLevelClickthroughUrl?: string;
  trackingPixels?: string[];
  creatives: Creative[];
  shouldShowCreativeMetadata: boolean;
}

export const ViewPlacementMetadata = ({
  placementLevelClickthroughUrl,
  trackingPixels,
  creatives,
  shouldShowCreativeMetadata
}: ViewPlacementMetadataProps) => {
  // No need to show people the tracking pixels we always add. Find the non-standard ones
  // to show them which pixels they may have manually added to a placement.
  const nonStandardTrackingPixels = useMemo(
    () =>
      trackingPixels?.filter(
        pixel => !STANDARD_TRACKING_PIXEL_MATCHERS.some(matcher => pixel.includes(matcher))
      ) || [],
    [trackingPixels]
  );

  return (
    <div className='view-placement-metadata'>
      {(placementLevelClickthroughUrl || Boolean(nonStandardTrackingPixels.length)) && (
        <div className='placement-metadata'>
          <div className='metadata-header'>Placement Metadata</div>
          {placementLevelClickthroughUrl && (
            <div className='placement-metadata-container'>
              <div className='placement-metadata-label'>Placement-Level Clickthrough URL</div>
              {placementLevelClickthroughUrl === 'None' ? (
                <div>None</div>
              ) : (
                <OverlayTrigger
                  placement={Placement.TOP_START}
                  overlay={
                    <Tooltip className='url-tooltip'>{placementLevelClickthroughUrl}</Tooltip>
                  }
                >
                  <a href={placementLevelClickthroughUrl} className='url-text'>
                    {placementLevelClickthroughUrl}
                  </a>
                </OverlayTrigger>
              )}
            </div>
          )}
          {Boolean(nonStandardTrackingPixels.length) && (
            <div className='placement-metadata-container'>
              <div className='placement-metadata-label'>Non-Standard Tracking Pixels</div>
              {nonStandardTrackingPixels.map((pixel, i) => (
                <OverlayTrigger
                  key={pixel}
                  placement={Placement.TOP_START}
                  overlay={<Tooltip className='url-tooltip'>{pixel}</Tooltip>}
                >
                  <div className='url-text'>{`${i + 1}) ${pixel}`}</div>
                </OverlayTrigger>
              ))}
            </div>
          )}
        </div>
      )}

      {shouldShowCreativeMetadata && (
        <div className='rotation-metadata'>
          <div className='metadata-header'>Creative Metadata</div>
          {creatives.map(creative => (
            <div key={creative.isci}>
              {(creative.creativeClickthroughUrl || creative.audioCompanion) && (
                <div className='rotation-metadata-outer-container'>
                  <div className='isci-label'>
                    <div className='isci'> {creative.isci}</div>
                    <div className='creative-name'>{`(${creative.creative} - ${creative.length}s)`}</div>
                  </div>

                  <div className='rotation-metadata-content'>
                    {creative.creativeClickthroughUrl && (
                      <div className='rotation-metadata-inner-container'>
                        <div className='rotation-metadata-label'>
                          Creative-Level Clickthrough URL
                        </div>
                        {creative.creativeClickthroughUrl === 'None' ? (
                          <div>None</div>
                        ) : (
                          <OverlayTrigger
                            placement={Placement.TOP_START}
                            overlay={
                              <Tooltip className='url-tooltip'>
                                {creative.creativeClickthroughUrl}
                              </Tooltip>
                            }
                          >
                            <a href={creative.creativeClickthroughUrl} className='url-text'>
                              {creative.creativeClickthroughUrl}
                            </a>
                          </OverlayTrigger>
                        )}
                      </div>
                    )}
                    {creative.audioCompanion && (
                      <div className='rotation-metadata-inner-container'>
                        <div className='rotation-metadata-label'>Audio Companion</div>
                        <div>{creative.audioCompanion || 'None'}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
